import { jsxs, jsx } from "react/jsx-runtime";
import { token } from "@/styled-system/tokens";
import { Paragraph } from "@zillow/constellation-10";
import { abbr } from "../../node_modules/@zillow/formatters/dist/formatters.esm.esm.js";
import React, { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { getWholeDollars } from "../../utils/zhl-calculator.esm.js";
import PropInterpolatorWrapper from "../PaymentDonutChart/Chart/common/PropInterpolator.esm.js";
import LineChart from "../PaymentDonutChart/LineChart/LineChart.esm.js";
import withDimensions from "../PaymentDonutChart/withDimensions.esm.js";
import { useLastValidAmortizationValues, TERM_TO_YEAR, safe } from "./AmortizationCalculator.utils.esm.js";
const GRAPH_HEIGHT = 275;
const legendIndexFormatter = (x) => x === null ? x : `${x} month${x !== 1 ? "s" : ""}`;
const xAxisFormatter = safe((x) => x + " mo");
const yAxisFormatter = safe((x) => "$" + abbr(x));
const getAccessibleValue = (index, message) => index % 12 === 0 ? message : "";
const AmortizationLineChartBase = ({
  containerWidth
}) => {
  const backgroundDarkGreen = token.var("colors.backgroundDarkGreen");
  const backgroundDarkAqua = token.var("colors.backgroundDarkAqua");
  const backgroundDarkPurple = token.var("colors.backgroundDarkPurple");
  const borderDark = token.var("colors.borderDark");
  const textDark = token.var("colors.textDark");
  const sansSerif = token.var("fonts.sansSerif");
  const colors = [backgroundDarkGreen, backgroundDarkAqua, backgroundDarkPurple];
  const interactionEventCmsInfo = {
    label: "chart slider",
    block_type: "slider"
  };
  const form = useFormContext();
  const lastValidValues = useLastValidAmortizationValues(form);
  const chartDataProps = lastValidValues.calculations.amortization;
  const lineData = useMemo(() => {
    const {
      principal,
      interest,
      remaining
    } = chartDataProps.reduce((acc, {
      totalPrincipal,
      totalInterest,
      remainingBalance
    }, i) => ({
      principal: [...acc.principal, {
        x: i,
        y: totalPrincipal,
        accessibleValue: getAccessibleValue(i, "Principal paid")
      }],
      interest: [...acc.interest, {
        x: i,
        y: totalInterest,
        accessibleValue: getAccessibleValue(i, "Interest paid")
      }],
      remaining: [...acc.remaining, {
        x: i,
        y: remainingBalance,
        accessibleValue: getAccessibleValue(i, "Remaining balance")
      }]
    }), {
      principal: [],
      interest: [],
      remaining: []
    });
    return [{
      name: "Principal",
      formatter: (value) => (
        // eslint-disable-next-line no-restricted-globals
        isNaN(value) || value === null ? null : getWholeDollars(value)
      ),
      values: principal
    }, {
      name: "Interest",
      formatter: (value) => (
        // eslint-disable-next-line no-restricted-globals
        isNaN(value) || value === null ? null : getWholeDollars(value)
      ),
      values: interest
    }, {
      name: "Remaining",
      formatter: (value) => (
        // eslint-disable-next-line no-restricted-globals
        isNaN(value) || value === null ? null : getWholeDollars(value)
      ),
      values: remaining
    }];
  }, [chartDataProps]);
  return /* @__PURE__ */ jsxs(React.Fragment, { children: [
    /* @__PURE__ */ jsxs(Paragraph, { css: {
      marginTop: "tighter"
    }, children: [
      "The amortization chart shows the trend between interest paid and principal paid in comparison to the remaining loan balance. Based on the details provided in the amortization calculator, over ",
      TERM_TO_YEAR[lastValidValues.fields.term],
      " years you’ll pay ",
      getWholeDollars(lastValidValues.calculations.totalPayment),
      " ",
      "in principal and interest."
    ] }),
    /* @__PURE__ */ jsx("div", { style: {
      position: "relative",
      paddingTop: "100px"
    }, children: /* @__PURE__ */ jsx(LineChart, { containerWidth, legend: false, legendIndexFormatter, margins: {
      top: 10,
      right: 50,
      bottom: 40,
      left: 20
    }, data: lineData, colors: (i) => colors[i], xHideOrigin: true, xAxisTickCount: 3, xAxisFormatter, yAxisTickCount: 4, yAxisFormatter, lineWidth: 2, axesColor: borderDark, tickTextStroke: textDark, height: GRAPH_HEIGHT, displayDataPoints: false, "aria-label": "Mortgage payoff schedule chart", xAxisLabel: "Months", yAxisLabel: "Paid amount", "aria-hidden": "true", fontFamily: sansSerif, interactionEventCmsInfo }) })
  ] });
};
const AmortizationLineChart = PropInterpolatorWrapper(withDimensions(AmortizationLineChartBase, {
  defaultWidth: 550,
  defaultHeight: null
}));
export {
  AmortizationLineChart as default
};
